"use client";
import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSession } from "next-auth/react";
import { useNextStep } from "nextstepjs";
import { api } from "~/trpc/react";
import { TourType, type TourProgress } from "~/types/tour";

interface TourContextType {
  // State
  currentTour: string | null;
  tourProgress: TourProgress | null;

  // Actions
  startTour: (tourType: TourType) => void;
  completeTour: (tourType: TourType) => void;
  closeTour: () => void;
}

const TourContext = createContext<TourContextType | null>(null);

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { startNextStep, closeNextStep, currentTour } = useNextStep();
  const { status } = useSession();
  const isAuthenticated = status === "authenticated";
  const { data: tourProgress } = api.user.getTourProgress.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const updateTourProgress = api.user.updateTourProgress.useMutation();
  const utils = api.useUtils();

  // These hook results can change identity as their state changes. Keep the
  // context actions stable so effects that depend on them do not rerun for
  // every mutation-state render.
  const actionsRef = useRef({
    startNextStep,
    closeNextStep,
    updateTourProgress: updateTourProgress.mutate,
    setTourProgress: utils.user.getTourProgress.setData,
  });
  actionsRef.current = {
    startNextStep,
    closeNextStep,
    updateTourProgress: updateTourProgress.mutate,
    setTourProgress: utils.user.getTourProgress.setData,
  };

  const stateRef = useRef({ currentTour, tourProgress });
  stateRef.current = { currentTour, tourProgress };

  // Completion can arrive both from NextStep and from the highlighted UI
  // action. Only persist the first completion for a given tour.
  const completingToursRef = useRef(new Set<TourType>());

  const startTour = useCallback((tourType: TourType) => {
    if (
      stateRef.current.currentTour === tourType ||
      stateRef.current.tourProgress?.[tourType]
    ) {
      return;
    }

    actionsRef.current.startNextStep(tourType);
  }, []);

  const completeTour = useCallback((tourType: TourType) => {
    if (
      stateRef.current.tourProgress?.[tourType] ||
      completingToursRef.current.has(tourType)
    ) {
      return;
    }

    completingToursRef.current.add(tourType);
    actionsRef.current.updateTourProgress(
      {
        tourStep: tourType,
        completed: true,
      },
      {
        onSuccess: (updatedProgress) => {
          actionsRef.current.setTourProgress(undefined, updatedProgress);
          actionsRef.current.closeNextStep();
        },
        onError: () => {
          completingToursRef.current.delete(tourType);
        },
      },
    );
  }, []);

  const closeTour = useCallback(() => {
    actionsRef.current.closeNextStep();
  }, []);

  const value: TourContextType = useMemo(
    () => ({
      currentTour,
      tourProgress: tourProgress ?? null,
      startTour,
      completeTour,
      closeTour,
    }),
    [currentTour, tourProgress, startTour, completeTour, closeTour],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
}
